import { UNIFIED_SOURCE, CLUSTER_IMG_SOURCE } from '../utils/map-constants.js'
import { ENTITY_CONFIGS } from '../data/entity-configs.js'
import { maybeRecolorSvg, getBitmapCacheEntry, setBitmapCacheEntry } from '../utils/icon-registry.js'

var DPR = window.devicePixelRatio || 1

function readVar(name) {
  return getComputedStyle(document.documentElement)
    .getPropertyValue('--kln-' + name).trim()
}

// Aplica un alpha absoluto (0..1) a un color hex (#RRGGBB) o rgb/rgba.
// Usado por el indicador de alerta del cluster para pintar el borde al
// 60% sin depender de que los tokens del tema declaren variantes alpha.
function _applyAlpha(color, alpha) {
  if (!color) return color
  var c = color.trim()
  // #RRGGBB
  var hexMatch = c.match(/^#([0-9a-fA-F]{6})$/)
  if (hexMatch) {
    var r = parseInt(hexMatch[1].slice(0, 2), 16)
    var g = parseInt(hexMatch[1].slice(2, 4), 16)
    var b = parseInt(hexMatch[1].slice(4, 6), 16)
    return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')'
  }
  // rgb(...) / rgba(...)
  var rgbMatch = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
  if (rgbMatch) {
    return 'rgba(' + rgbMatch[1] + ',' + rgbMatch[2] + ',' + rgbMatch[3] + ',' + alpha + ')'
  }
  return c
}

var BG_COLOR     = readVar('bg-overlay')    || 'rgba(10, 14, 26, 0.88)'
var BORDER_COLOR = readVar('border-normal') || 'rgba(255, 255, 255, 0.12)'
var TEXT_COLOR   = readVar('text')          || 'rgba(255, 255, 255, 0.92)'
var ICON_COLOR   = '#FFFFFF'                // blanco puro para recolorización SVG
var FONT = '400 ' + (11 * DPR) + 'px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
var FONT_SIZE = 11
var BORDER_RADIUS = 10
var PADDING_X = 10
var PADDING_Y = 8
var COL_GAP = 12
var ROW_GAP = 6
var ICON_SIZE = 20
var ICON_TEXT_GAP = 5
var COLS = 2

var ICON_PACK = import.meta.env.VITE_MAP_ICON_PACK || 'kalan'
var USE_GENERATED = String(import.meta.env.VITE_USE_GENERATED_ICONS || '').toLowerCase() === 'true'

// Cache bounded — cada entry es una textura WebGL ~150x80@DPR2 = ~192 KB.
// Sin tope, con zoom intensivo en cluster mode los typeCounts cambian
// constantemente (~297/min medido) y la cache crece sin freno (visto:
// 4061 entradas en 5 min ≈ 780 MB en bitmaps).
//
// 3000 entradas = ~575 MB techo. Hit rate medido: 94.1% — los reusos
// son frecuentes y vale el cache. La eviction es FIFO (Map mantiene
// insertion order) y DIFERIDA: cuando se evicta una entrada, su imageId
// va a pendingEviction; se ejecuta map.removeImage solo cuando se
// confirma que ningún cluster visible la referencia (al final del
// próximo updateClusterImages).
//
// Si una entrada en pendingEviction se vuelve a necesitar antes de ser
// removida fisicamente, se restaura al cache (rescue) — barato y evita
// regenerar el bitmap.
var MAX_CLUSTER_IMAGES = 3000
var imageCache = new Map()
var pendingEviction = new Map()  // key → imageId (esperan removeImage)
var iconBitmapCache = {} // entityType → HTMLCanvasElement

// ─── Telemetría del cache (decisión: ¿vale la pena el cache?) ───────────
// Counters acumulativos; ventana de tiempo se calcula con startedAt.
// Expuestos via window.__clusterCacheStats / .reset() / .report().
var cacheStats = {
  hits: 0,            // imageCache.has(key) → return inmediato
  recoveryHits: 0,    // map.hasImage(id) tras cache JS frío (raro: solo cambio tema)
  misses: 0,          // genera bitmap nuevo + addImage
  missMs: 0,          // tiempo TOTAL gastado en misses (canvas + getImageData + addImage)
  missMaxMs: 0,       // peor miss observado
  evictions: 0,       // imágenes evictadas (si LRU está activo en el futuro)
  startedAt: Date.now(),
}
if (typeof window !== 'undefined') {
  window.__clusterCacheStats = function() {
    var elapsed = (Date.now() - cacheStats.startedAt) / 1000
    var totalLookups = cacheStats.hits + cacheStats.recoveryHits + cacheStats.misses
    return {
      elapsed_sec: Math.round(elapsed),
      hits: cacheStats.hits,
      recovery_hits: cacheStats.recoveryHits,
      misses: cacheStats.misses,
      total_lookups: totalLookups,
      hit_rate_pct: totalLookups > 0
        ? Math.round((cacheStats.hits + cacheStats.recoveryHits) / totalLookups * 1000) / 10
        : 0,
      miss_total_ms: Math.round(cacheStats.missMs),
      miss_avg_ms: cacheStats.misses > 0
        ? Math.round(cacheStats.missMs / cacheStats.misses * 100) / 100
        : 0,
      miss_max_ms: Math.round(cacheStats.missMaxMs * 100) / 100,
      misses_per_min: elapsed > 0
        ? Math.round(cacheStats.misses / (elapsed / 60) * 10) / 10
        : 0,
      cpu_ms_per_min: elapsed > 0
        ? Math.round(cacheStats.missMs / (elapsed / 60) * 10) / 10
        : 0,
      cache_size: imageCache.size,
      cache_max: MAX_CLUSTER_IMAGES,
      pending_eviction: pendingEviction.size,
      evictions: cacheStats.evictions,
    }
  }
  window.__clusterCacheStatsReset = function() {
    cacheStats.hits = 0
    cacheStats.recoveryHits = 0
    cacheStats.misses = 0
    cacheStats.missMs = 0
    cacheStats.missMaxMs = 0
    cacheStats.evictions = 0
    cacheStats.startedAt = Date.now()
    console.log('[clusterCacheStats] counters reseteados — el cache JS sigue intacto')
  }
  window.__clusterCacheStatsReport = function() {
    var s = window.__clusterCacheStats()
    console.table(s)
    var verdict = ''
    if (s.total_lookups < 100) verdict = '🟡 muestra insuficiente — espera más actividad'
    else if (s.hit_rate_pct >= 90) verdict = '🟢 cache vale la pena (hit rate alto, MISSes acotados)'
    else if (s.hit_rate_pct >= 70) verdict = '🟡 cache parcial — evaluar bajar techo o cambiar diseño'
    else verdict = '🔴 cache no aporta — considerar removerlo'
    console.log('[verdict]', verdict)
    return s
  }
}

/**
 * Pre-load entity type icons as Canvas bitmaps for cluster rendering.
 * Call once after map icons are registered.
 *
 * Con USE_GENERATED=true: consume SVGs pre-pintados de
 * theming/generated/<tema-activo>/ (mismos que el mapa) — los cluster
 * cards reflejan la paleta del sistema declarativo.
 * Legacy: lee de map-argos/ + recolorea en runtime vía maybeRecolorSvg.
 */
export function warmClusterIconCache() {
  var svgKalan = import.meta.glob('/src/assets/map/**/*.svg', { as: 'raw', eager: true })
  var svgArgos = import.meta.glob('/src/assets/map-argos/**/*.svg', { as: 'raw', eager: true })
  var svgGenerated = import.meta.glob('/src/components/map-v2/theming/generated/**/*.svg', { as: 'raw', eager: true })

  // Cuando el sistema de temas declarativo está activo, usamos los
  // SVGs ya pintados del tema actual. El state suffix que elegimos
  // para cluster es `online` (el color "normal" de cada capa).
  var usingGenerated = false
  var svgModules
  if (USE_GENERATED && Object.keys(svgGenerated).length > 0) {
    var activeTheme = 'desaturado'
    try {
      var persisted = window && window.localStorage && window.localStorage.getItem('kalan:theme')
      if (persisted) activeTheme = persisted
    } catch (_) { /* ignore */ }
    var prefix = '/src/components/map-v2/theming/generated/' + activeTheme + '/'
    svgModules = {}
    var allKeys = Object.keys(svgGenerated)
    for (var k = 0; k < allKeys.length; k++) {
      if (allKeys[k].startsWith(prefix)) {
        svgModules[allKeys[k]] = svgGenerated[allKeys[k]]
      }
    }
    usingGenerated = Object.keys(svgModules).length > 0
  }
  if (!usingGenerated) {
    svgModules = ICON_PACK === 'argos' ? svgArgos : svgKalan
  }

  // Mapeo tipo → nombre del SVG representativo.
  // En modo generated usamos el sufijo lógico del estado "normal" (online);
  // en legacy el sufijo estructural `_up` del pack argos/kalan.
  var suffix = usingGenerated ? 'online' : 'up'
  var entityIconMap = {
    device: 'patrulla_municipal_' + suffix,
    bus: 'bus_' + suffix,
    camera: 'camera_' + suffix,
    lpr: 'lpr_' + suffix,
    incident: 'incident_seguridad_' + (usingGenerated ? 'media' : 'up'),
    sensor: 'sensor_' + suffix,
    external_sensor: 'external_sensor_' + suffix,
    external_sensor_route: 'external_sensor_' + suffix,
    external_device: 'external_device_' + suffix,
    presa: 'presa_' + suffix,
    sismo: 'sismo_' + suffix,
    fire: 'fire_' + suffix,
    cpr: 'cpr_' + suffix,
    reportant: 'reportant_' + suffix,
    sos_call: 'sos_' + (usingGenerated ? 'WAITING_OPERATOR' : 'up'),
    route_lpr: 'route_lpr_' + suffix,
  }

  var entries = Object.entries(svgModules)
  var svgByName = {}
  for (var i = 0; i < entries.length; i++) {
    var name = entries[i][0].split('/').pop().replace('.svg', '')
    svgByName[name] = entries[i][1]
  }

  var types = Object.keys(entityIconMap)
  for (var t = 0; t < types.length; t++) {
    var type = types[t]
    var svgName = entityIconMap[type]
    if (!svgName || !svgByName[svgName]) continue

    var svgContent = svgByName[svgName]
    var bitmap = svgToBitmap(svgName, svgContent, ICON_SIZE * DPR, ICON_COLOR, usingGenerated)
    if (bitmap) iconBitmapCache[type] = bitmap
  }
}

/**
 * Convert SVG string to a Canvas bitmap.
 * Generated: el SVG ya viene pintado; se usa tal cual.
 * Argos: se recolorea el bg al token de entidad del tema activo via
 * `maybeRecolorSvg` (mismo color que el marker del mapa).
 * Kalan: se recolorean todos los fills a blanco para contraste del card.
 */
function svgToBitmap(svgName, svgString, size, color, isGenerated) {
  try {
    var colored
    if (isGenerated) {
      colored = svgString
    } else if (ICON_PACK === 'argos') {
      colored = maybeRecolorSvg(svgName, svgString)
    } else {
      colored = svgString
        .replace(/fill="[^"]*"/g, 'fill="' + color + '"')
        .replace(/stroke="[^"]*"/g, 'stroke="' + color + '"')
        .replace(/currentColor/g, color)
    }

    var canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    var ctx = canvas.getContext('2d')

    var img = new Image()
    var blob = new Blob([colored], { type: 'image/svg+xml' })
    var url = URL.createObjectURL(blob)

    // Synchronous trick: draw to canvas via a data URL
    // We'll use the async approach and cache the result
    img.src = url

    // For synchronous rendering, we try to draw immediately
    // If the image hasn't loaded, we'll get an empty canvas (fallback to dot)
    try {
      ctx.drawImage(img, 0, 0, size, size)
      URL.revokeObjectURL(url)
      // Check if anything was drawn
      var pixel = ctx.getImageData(Math.floor(size / 2), Math.floor(size / 2), 1, 1).data
      if (pixel[3] > 0) return canvas
    } catch (e) {
      // Image not loaded synchronously, ignore
    }
    URL.revokeObjectURL(url)
    return null
  } catch (e) {
    return null
  }
}

/**
 * Async version: pre-load all icon bitmaps. Call on startup.
 */
// Qué SVG hay disponibles: los pre-pintados del tema activo si el sistema de
// temas está encendido y trae archivos, si no el pack argos/kalan. La usan
// warmClusterIconCacheAsync y warmClusterSlotIconsAsync, para que un renglón
// de slot salga del mismo sitio que la tarjeta del mapa principal.
function resolveSvgModules() {
  var svgKalan = import.meta.glob('/src/assets/map/**/*.svg', { as: 'raw', eager: true })
  var svgArgos = import.meta.glob('/src/assets/map-argos/**/*.svg', { as: 'raw', eager: true })
  var svgGenerated = import.meta.glob('/src/components/map-v2/theming/generated/**/*.svg', { as: 'raw', eager: true })

  var usingGenerated = false
  var svgModules
  var activeTheme = 'desaturado'
  if (USE_GENERATED && Object.keys(svgGenerated).length > 0) {
    try {
      var persisted = window && window.localStorage && window.localStorage.getItem('kalan:theme')
      if (persisted) activeTheme = persisted
    } catch (_) { /* ignore */ }
    var prefix = '/src/components/map-v2/theming/generated/' + activeTheme + '/'
    svgModules = {}
    var allKeys = Object.keys(svgGenerated)
    for (var k = 0; k < allKeys.length; k++) {
      if (allKeys[k].startsWith(prefix)) {
        svgModules[allKeys[k]] = svgGenerated[allKeys[k]]
      }
    }
    usingGenerated = Object.keys(svgModules).length > 0
  }
  if (!usingGenerated) {
    svgModules = ICON_PACK === 'argos' ? svgArgos : svgKalan
  }
  return { svgModules: svgModules, usingGenerated: usingGenerated, activeTheme: activeTheme }
}

// Sufijo del pack viejo para un estado semántico. El generado usa el estado
// tal cual (`camera_online`); argos/kalan, el sufijo estructural (`camera_up`).
var LEGACY_SUFFIX = { online: 'up', offline: 'down' }

/**
 * Bitmaps de los renglones que declara `slots` (ver updateClusterImages).
 * Cada slot trae `key`, `icon` y `status` semántico (online/offline). El SVG
 * se busca como el de la tarjeta del mapa principal —`<icon>_<sufijo>`, sin
 * modo día/noche— y, si no existe, `<icon>_day_<sufijo>`: en el tema la
 * variante caída solo viene con modo, y `camera_online` es el mismo archivo
 * que `camera_day_online`. Un slot sin SVG queda con el punto de color.
 */
export async function warmClusterSlotIconsAsync(slots) {
  var res = resolveSvgModules()
  var svgByName = {}
  var entries = Object.entries(res.svgModules)
  for (var i = 0; i < entries.length; i++) {
    svgByName[entries[i][0].split('/').pop().replace('.svg', '')] = entries[i][1]
  }
  var promises = []
  for (var s = 0; s < slots.length; s++) {
    var slot = slots[s]
    var suffix = res.usingGenerated ? slot.status : (LEGACY_SUFFIX[slot.status] || slot.status)
    var candidates = [slot.icon + '_' + suffix, slot.icon + '_day_' + suffix]
    for (var c = 0; c < candidates.length; c++) {
      if (svgByName[candidates[c]]) {
        promises.push(loadIconBitmap(slot.key, candidates[c], svgByName[candidates[c]], res.usingGenerated))
        break
      }
    }
  }
  await Promise.all(promises)
}

export async function warmClusterIconCacheAsync() {
  var resolved = resolveSvgModules()
  var usingGenerated = resolved.usingGenerated
  var svgModules = resolved.svgModules
  var activeTheme = resolved.activeTheme

  var suffix = usingGenerated ? 'online' : 'up'
  var entityIconMap = {
    device: 'patrulla_municipal_' + suffix,
    bus: 'bus_' + suffix,
    camera: 'camera_' + suffix,
    lpr: 'lpr_' + suffix,
    incident: 'incident_seguridad_' + (usingGenerated ? 'media' : 'up'),
    sensor: 'sensor_' + suffix,
    external_sensor: 'external_sensor_' + suffix,
    external_sensor_route: 'external_sensor_' + suffix,
    external_device: 'external_device_' + suffix,
    presa: 'presa_' + suffix,
    sismo: 'sismo_' + suffix,
    fire: 'fire_' + suffix,
    cpr: 'cpr_' + suffix,
    reportant: 'reportant_' + suffix,
    sos_call: 'sos_' + (usingGenerated ? 'WAITING_OPERATOR' : 'up'),
    route_lpr: 'route_lpr_' + suffix,
  }

  // FAST PATH: cache hit. Reusamos el patron de IDB de icon-registry.
  // Los bitmaps cluster son los mismos que ya rasterizamos la vez
  // anterior con la misma combinacion theme+pack+DPR.
  var cacheKey = 'cluster:' + activeTheme + ':' + ICON_PACK + ':' + DPR
  var cacheHash = String(Object.keys(svgModules).length) + ':' + suffix
  try {
    var cached = await getBitmapCacheEntry(cacheKey, cacheHash)
    if (cached && cached.bitmaps) {
      var keys = Object.keys(cached.bitmaps)
      for (var ck = 0; ck < keys.length; ck++) {
        iconBitmapCache[keys[ck]] = cached.bitmaps[keys[ck]]
      }
      try {
        if (window.__bootProfile) {
          window.__bootProfile.custom = window.__bootProfile.custom || {}
          window.__bootProfile.custom.clusterIconsCacheHit = true
          window.__bootProfile.custom.clusterIconsCount = keys.length
        }
      } catch (_) {}
      return
    }
  } catch (_) {}

  // SLOW PATH: rasterizar en serie y cachear el resultado.
  var svgByName = {}
  var entries = Object.entries(svgModules)
  for (var i = 0; i < entries.length; i++) {
    var name = entries[i][0].split('/').pop().replace('.svg', '')
    svgByName[name] = entries[i][1]
  }

  var promises = []
  var types = Object.keys(entityIconMap)
  for (var t = 0; t < types.length; t++) {
    var type = types[t]
    var svgName = entityIconMap[type]
    if (!svgName || !svgByName[svgName]) continue
    promises.push(loadIconBitmap(type, svgName, svgByName[svgName], usingGenerated))
  }

  await Promise.all(promises)

  // Cachear los bitmaps que se rasterizaron (los que quedaron en
  // iconBitmapCache). HTMLCanvasElement no es structured-cloneable —
  // convertimos a ImageBitmap via createImageBitmap antes de guardar.
  try {
    var toCache = {}
    var cacheTypes = Object.keys(iconBitmapCache)
    for (var cty = 0; cty < cacheTypes.length; cty++) {
      var bm = iconBitmapCache[cacheTypes[cty]]
      if (bm && typeof createImageBitmap === 'function') {
        try {
          var ib = await createImageBitmap(bm)
          toCache[cacheTypes[cty]] = ib
          // El consumer (drawImage) acepta tanto canvas como ImageBitmap.
          // Reemplazamos en el cache runtime con el ImageBitmap para que
          // sea consistente con lo que va al IDB y con futuras lecturas.
          iconBitmapCache[cacheTypes[cty]] = ib
        } catch (_) {}
      }
    }
    if (Object.keys(toCache).length > 0) {
      setBitmapCacheEntry(cacheKey, cacheHash, toCache).catch(function() {})
    }
  } catch (_) {}

  try {
    if (window.__bootProfile) {
      window.__bootProfile.custom = window.__bootProfile.custom || {}
      window.__bootProfile.custom.clusterIconsCacheHit = false
      window.__bootProfile.custom.clusterIconsCount = Object.keys(iconBitmapCache).length
    }
  } catch (_) {}
}

function loadIconBitmap(entityType, svgName, svgString, isGenerated) {
  return new Promise(function(resolve) {
    var colored
    if (isGenerated) {
      colored = svgString
    } else if (ICON_PACK === 'argos') {
      colored = maybeRecolorSvg(svgName, svgString)
    } else {
      colored = svgString
        .replace(/fill="[^"]*"/g, 'fill="' + ICON_COLOR + '"')
        .replace(/stroke="[^"]*"/g, 'stroke="' + ICON_COLOR + '"')
        .replace(/currentColor/g, ICON_COLOR)
    }

    var size = ICON_SIZE * DPR
    var img = new Image(size, size)
    var blob = new Blob([colored], { type: 'image/svg+xml' })
    var url = URL.createObjectURL(blob)

    img.onload = function() {
      var canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      var ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, size, size)
      URL.revokeObjectURL(url)
      iconBitmapCache[entityType] = canvas
      resolve()
    }
    img.onerror = function() {
      URL.revokeObjectURL(url)
      resolve()
    }
    img.src = url
  })
}

/**
 * Query visible clusters, render breakdown images, and update the
 * secondary cluster-img-source so the symbol layer can display them.
 *
 * `opts` (opcional) deja usar este mismo renderer en otro mapa —el del
 * reporte de cámaras—: `source` e `imgSource` son sus fuentes, y `slots`
 * sustituye el desglose por tipo de entidad por renglones propios (ver
 * extractSlotCounts y warmClusterSlotIconsAsync). Sin `opts`, el mapa
 * principal, exactamente como antes.
 */
export function updateClusterImages(map, opts) {
  try { if (window.__memTracer) window.__memTracer.tick('cluster.updateImages') } catch (_) {}
  var sourceId = (opts && opts.source) || UNIFIED_SOURCE
  var imgSourceId = (opts && opts.imgSource) || CLUSTER_IMG_SOURCE
  var slots = opts && opts.slots
  var source = map.getSource(sourceId)
  if (!source) return

  var imgSource = map.getSource(imgSourceId)
  if (!imgSource) return

  var clusters = map.querySourceFeatures(sourceId, { filter: ['has', 'point_count'] })
  if (clusters.length === 0) {
    imgSource.setData({ type: 'FeatureCollection', features: [] })
    // Aún sin clusters visibles, las pending no son referenciadas por
    // ninguna feature → seguro purgarlas.
    flushPendingEvictions(map, {})
    return
  }

  var seen = {}
  var unique = []
  for (var i = 0; i < clusters.length; i++) {
    var cid = clusters[i].id
    if (cid != null && seen[cid]) continue
    if (cid != null) seen[cid] = true
    unique.push(clusters[i])
  }

  var features = []
  // activeImageIds: los imageIds que el setData de ESTE round registrará.
  // Si una entrada en pendingEviction está aquí, NO se borra (rescue).
  var activeImageIds = Object.create(null)
  for (var j = 0; j < unique.length; j++) {
    var cluster = unique[j]
    var props = cluster.properties
    var typeCounts = slots ? extractSlotCounts(props, slots) : extractTypeCounts(props)
    if (typeCounts.length === 0) continue

    // MARKERS-DESIGN-SPEC.md §7: el cluster card muestra borde + punto
    // rojo cuando contiene al menos 1 feature en alerta. Si hay SOS
    // activos (sos_alert_count ≥ 1), el color sube a alert-sos.
    var alertState = 'none'
    if ((props.sos_alert_count || 0) > 0) alertState = 'sos'
    else if ((props.alert_count || 0) > 0) alertState = 'danger'

    var imageId = ensureClusterImage(map, typeCounts, alertState)
    activeImageIds[imageId] = true
    features.push({
      type: 'Feature',
      geometry: cluster.geometry,
      properties: { imageId: imageId },
    })
  }

  imgSource.setData({ type: 'FeatureCollection', features: features })

  // Eviction diferida: borrar de MapLibre las texturas que están en
  // pendingEviction y NO aparecen en el setData que acabamos de hacer.
  // Para las que SÍ aparecen, pendingEviction ya las rescató al cache.
  flushPendingEvictions(map, activeImageIds)
}

// Expone el tamaño del cache para mem-tracer.js. Lo dejamos como
// función global para evitar exportar una variable mutable.
if (typeof window !== 'undefined') {
  window.__clusterImageCacheSize = function() { return imageCache.size }

  // Para análisis exhaustivo del cache: lista de keys + edad aproximada.
  // El Map mantiene insertion order, así que index 0 = más viejo.
  window.__clusterImageCacheDump = function() {
    var arr = []
    var i = 0
    imageCache.forEach(function(imageId, key) {
      arr.push({ idx: i++, key: key, imageId: imageId })
    })
    return arr
  }

  // Análisis de patrones: ¿las claves se concentran en pocas combinaciones
  // (cache muy reusable) o son todas únicas (cache acumulador)?
  window.__clusterImageCachePatterns = function() {
    var keys = []
    imageCache.forEach(function(_, k) { keys.push(k) })
    // Distribución por # de tipos en la combinación
    var byTypeCount = {}
    var byAlertState = { none: 0, danger: 0, sos: 0 }
    keys.forEach(function(k) {
      var parts = k.split('|')
      var combo = parts[0]
      var state = parts[1] || 'none'
      var nTypes = combo.split(',').length
      byTypeCount[nTypes] = (byTypeCount[nTypes] || 0) + 1
      if (byAlertState[state] != null) byAlertState[state]++
    })
    return { total: keys.length, byTypeCount: byTypeCount, byAlertState: byAlertState }
  }
}

export function clearClusterImageCache(map) {
  // Remove old cluster images from MapLibre (cache + pending eviction)
  if (map) {
    imageCache.forEach(function(imageId) {
      if (map.hasImage(imageId)) {
        try { map.removeImage(imageId) } catch (e) { /* ignore */ }
      }
    })
    pendingEviction.forEach(function(imageId) {
      if (map.hasImage(imageId)) {
        try { map.removeImage(imageId) } catch (e) { /* ignore */ }
      }
    })
  }
  imageCache.clear()
  pendingEviction.clear()
  // Also clear icon bitmap cache so they re-render at new size
  Object.keys(iconBitmapCache).forEach(function(k) { delete iconBitmapCache[k] })
}

// ─── Internal helpers ─────────────────────────────────────────

function extractTypeCounts(props) {
  var result = []
  var entries = Object.entries(ENTITY_CONFIGS)
  // Modo del cluster gobernado por el tema (CSS var --kln-cluster-mode).
  //   'monochrome' → todos los slots pintan con --kln-accent (tactical).
  //   'colorful'   → cada entity usa su color propio (default).
  var mode = (getComputedStyle(document.documentElement)
    .getPropertyValue('--kln-cluster-mode') || '').trim() || 'colorful'
  var monoColor = null
  if (mode === 'monochrome') {
    monoColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--kln-accent').trim() || '#00FF88'
  }
  for (var i = 0; i < entries.length; i++) {
    var type = entries[i][0]
    var config = entries[i][1]
    var count = props[type + '_count'] || 0
    if (count > 0) {
      result.push({
        type: type,
        count: count,
        color: monoColor || config.color,
        label: config.label,
      })
    }
  }
  result.sort(function(a, b) { return b.count - a.count })
  return result
}

// Renglones fijos en vez del desglose por tipo: cada slot lee su propiedad
// de cluster (`count`) y se enseña en el orden declarado, no por tamaño —si
// no, activas y caídas cambiarían de lugar de una tarjeta a la vecina—. Un
// slot en cero no sale, igual que un tipo en cero en el mapa principal.
// `type` es la clave del slot: con ella se busca su bitmap y se arma la
// clave de la imagen, que así no choca con las del mapa principal.
function extractSlotCounts(props, slots) {
  var result = []
  for (var i = 0; i < slots.length; i++) {
    var count = props[slots[i].count] || 0
    if (count > 0) {
      result.push({ type: slots[i].key, count: count, color: slots[i].color, label: slots[i].label })
    }
  }
  return result
}

function ensureClusterImage(map, typeCounts, alertState) {
  // alertState: 'none' | 'danger' | 'sos' — decide si pintamos borde/punto
  // de alert y con qué color. Incluido en la cache key para que un cluster
  // que gana/pierde alertas regenere su imagen.
  var state = alertState || 'none'
  var key = typeCounts.map(function(t) { return t.type + ':' + t.count }).join(',') + '|' + state
  var imageId = 'cluster-' + key

  // El cache es del MÓDULO y sobrevive al mapa que registró la imagen: al
  // salir de /map y volver —o al abrir el mapa de un reporte que usa este
  // renderer— la entrada sigue aquí pero la imagen se fue con el mapa
  // destruido. Por eso un hit solo cuenta si la imagen está en ESTE mapa.
  // Medido 2026-09-28: al volver a /map, 5 de 19 tarjetas apuntaban a una
  // imagen inexistente y no se dibujaban.
  if (imageCache.has(key)) {
    if (map.hasImage(imageId)) {
      cacheStats.hits++
      return imageId
    }
    imageCache.delete(key)
  }

  // RESCUE: la entrada estaba marcada para eviction pero todavía vive en
  // MapLibre (no se llamó removeImage aún). Devuélvela al cache y cancela
  // su eviction. Evita regenerar el bitmap. Misma guarda que arriba.
  if (pendingEviction.has(key)) {
    pendingEviction.delete(key)
    if (map.hasImage(imageId)) {
      imageCache.set(key, imageId)
      cacheStats.hits++  // contado como hit — no regeneramos bitmap
      return imageId
    }
  }

  if (map.hasImage(imageId)) {
    cacheStats.recoveryHits++
    imageCache.set(key, imageId)
    return imageId
  }

  // MISS — generar bitmap. Medimos costo exacto.
  var t0 = performance.now()
  var result = renderClusterCanvas(typeCounts, state)
  var ctx = result.canvas.getContext('2d')
  var imageData = ctx.getImageData(0, 0, result.canvas.width, result.canvas.height)

  // Si estamos al tope, evictar la entrada más antigua del cache (FIFO —
  // Map mantiene insertion order). NO la borra de MapLibre todavía: la
  // pone en pendingEviction. El removeImage real se ejecuta al final de
  // updateClusterImages para garantizar que ninguna feature en
  // CLUSTER_IMG_SOURCE la referencia.
  if (imageCache.size >= MAX_CLUSTER_IMAGES) {
    var oldestKey = imageCache.keys().next().value
    if (oldestKey !== undefined) {
      var oldestImageId = imageCache.get(oldestKey)
      imageCache.delete(oldestKey)
      pendingEviction.set(oldestKey, oldestImageId)
    }
  }

  map.addImage(imageId, {
    width: result.canvas.width,
    height: result.canvas.height,
    data: new Uint8Array(imageData.data.buffer),
  }, { pixelRatio: DPR })

  imageCache.set(key, imageId)
  var dt = performance.now() - t0
  cacheStats.misses++
  cacheStats.missMs += dt
  if (dt > cacheStats.missMaxMs) cacheStats.missMaxMs = dt
  return imageId
}

// Aplica las evictions diferidas: para cada (key, imageId) en
// pendingEviction, si el activeImageIds NO la referencia, llama
// map.removeImage. Si la referencia (rescue tardío que vino del propio
// updateClusterImages actual), la rescata al cache.
function flushPendingEvictions(map, activeImageIds) {
  if (pendingEviction.size === 0) return
  var toDelete = []
  pendingEviction.forEach(function(imageId, key) {
    if (activeImageIds && activeImageIds[imageId]) {
      // Está en uso ahora mismo — rescatar al cache, NO borrar.
      imageCache.set(key, imageId)
    } else {
      toDelete.push({ key: key, imageId: imageId })
    }
  })
  for (var i = 0; i < toDelete.length; i++) {
    pendingEviction.delete(toDelete[i].key)
    if (map.hasImage(toDelete[i].imageId)) {
      try {
        map.removeImage(toDelete[i].imageId)
        cacheStats.evictions++
      } catch (e) { /* ignore */ }
    }
  }
}

/**
 * Render a cluster card with entity ICONS + counts in a 2-column grid.
 * Falls back to colored dots when icon bitmap is not available.
 * @param {Object[]} typeCounts
 * @param {string}   alertState  'none' | 'danger' | 'sos'
 */
function renderClusterCanvas(typeCounts, alertState) {
  var canvas = document.createElement('canvas')
  var ctx = canvas.getContext('2d')
  ctx.font = FONT

  var items = typeCounts.slice(0, 6)
  var numCols = items.length === 1 ? 1 : COLS
  var numRows = Math.ceil(items.length / numCols)

  var iconSizePx = ICON_SIZE * DPR

  // Measure column widths
  var colWidths = []
  for (var c = 0; c < numCols; c++) colWidths.push(0)

  for (var i = 0; i < items.length; i++) {
    var col = i % numCols
    var textW = ctx.measureText('' + items[i].count).width
    var cellW = iconSizePx + ICON_TEXT_GAP * DPR + textW
    if (cellW > colWidths[col]) colWidths[col] = cellW
  }

  var totalColWidth = 0
  for (var cw = 0; cw < colWidths.length; cw++) totalColWidth += colWidths[cw]
  var gapWidth = (numCols - 1) * COL_GAP * DPR

  var canvasW = Math.ceil(PADDING_X * 2 * DPR + totalColWidth + gapWidth)
  var rowH = Math.max(FONT_SIZE + ROW_GAP, ICON_SIZE + ROW_GAP) * DPR
  var canvasH = Math.ceil(PADDING_Y * 2 * DPR + numRows * rowH)

  if (canvasW < 48 * DPR) canvasW = 48 * DPR

  canvas.width = canvasW
  canvas.height = canvasH

  // Background
  ctx.fillStyle = BG_COLOR
  ctx.beginPath()
  ctx.roundRect(0, 0, canvasW, canvasH, BORDER_RADIUS * DPR)
  ctx.fill()

  // Border — MARKERS-DESIGN-SPEC.md §7.2: si el cluster contiene alerta
  // activa, el borde sale en alert-danger al 60% (o alert-sos al 60% si
  // hay SOS, prioridad máxima). Border-normal default en otros casos.
  var alertColor = null
  if (alertState === 'sos') {
    alertColor = readVar('alert-sos') || 'rgba(232, 74, 61, 0.95)'
  } else if (alertState === 'danger') {
    alertColor = readVar('alert-danger') || 'rgba(186, 77, 61, 0.95)'
  }
  var effectiveBorder = alertColor ? _applyAlpha(alertColor, 0.6) : BORDER_COLOR
  var effectiveBorderWidth = alertColor ? 1.5 : 1
  ctx.strokeStyle = effectiveBorder
  ctx.lineWidth = effectiveBorderWidth * DPR
  ctx.beginPath()
  ctx.roundRect(
    (effectiveBorderWidth / 2) * DPR,
    (effectiveBorderWidth / 2) * DPR,
    canvasW - effectiveBorderWidth * DPR,
    canvasH - effectiveBorderWidth * DPR,
    BORDER_RADIUS * DPR,
  )
  ctx.stroke()

  // Indicador: punto circular de 6px en alert color, esquina superior
  // derecha (§7.2 spec). Solo cuando alertState ≠ 'none'.
  if (alertColor) {
    var dotRadius = 3 * DPR
    var dotOffset = 7 * DPR
    ctx.fillStyle = alertColor
    ctx.beginPath()
    ctx.arc(canvasW - dotOffset, dotOffset, dotRadius, 0, Math.PI * 2)
    ctx.fill()
  }

  // Draw cells
  ctx.font = FONT
  ctx.textBaseline = 'middle'

  for (var idx = 0; idx < items.length; idx++) {
    var row = Math.floor(idx / numCols)
    var colIdx = idx % numCols

    var x = PADDING_X * DPR
    for (var pc = 0; pc < colIdx; pc++) {
      x += colWidths[pc] + COL_GAP * DPR
    }

    var y = PADDING_Y * DPR + row * rowH
    var centerY = y + rowH / 2

    // Try to draw icon bitmap, fallback to colored dot
    var bitmap = iconBitmapCache[items[idx].type]
    if (bitmap) {
      var iconY = centerY - iconSizePx / 2
      ctx.drawImage(bitmap, x, iconY, iconSizePx, iconSizePx)
    } else {
      // Fallback: colored dot
      var dotR = 4 * DPR
      ctx.fillStyle = items[idx].color
      ctx.beginPath()
      ctx.arc(x + iconSizePx / 2, centerY, dotR, 0, Math.PI * 2)
      ctx.fill()
    }

    // Count text
    ctx.fillStyle = TEXT_COLOR
    ctx.fillText('' + items[idx].count, x + iconSizePx + ICON_TEXT_GAP * DPR, centerY)
  }

  return { canvas: canvas, width: canvasW / DPR, height: canvasH / DPR }
}
